import { useEffect, useState } from 'react';

const THEME_KEY = 'smalt-gtb-theme';

function getStoredTheme(): 'light' | 'dark' | null {
  try {
    let stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    stored = sessionStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    // Migration from legacy key
    const legacy = localStorage.getItem('theme');
    if (legacy === 'light' || legacy === 'dark') {
      localStorage.setItem(THEME_KEY, legacy);
      localStorage.removeItem('theme');
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

function setStoredTheme(theme: 'light' | 'dark') {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    try {
      sessionStorage.setItem(THEME_KEY, theme);
    } catch {
      // Both blocked (e.g. private browsing)
    }
  }
}

// Initialize theme synchronously before React renders
function initializeTheme(): boolean {
  const stored = getStoredTheme();
  const root = document.documentElement;

  if (stored === 'light') {
    root.classList.remove('dark');
    return false;
  }
  if (stored === 'dark') {
    root.classList.add('dark');
    return true;
  }

  // Fallback: system preference
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  if (prefersDark) {
    root.classList.add('dark');
    setStoredTheme('dark');
    return true;
  }
  root.classList.remove('dark');
  setStoredTheme('light');
  return false;
}

initializeTheme();

export function useTheme() {
  // Read from storage/DOM on each mount so theme persists when navigating (SPA)
  const [isDark, setIsDark] = useState(() => {
    const stored = getStoredTheme();
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return document.documentElement.classList.contains('dark');
  });

  // Persist on change + sync from other tabs
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      setStoredTheme('dark');
    } else {
      root.classList.remove('dark');
      setStoredTheme('light');
    }
  }, [isDark]);

  // Listen for changes from other tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === THEME_KEY && e.newValue) {
        const nextDark = e.newValue === 'dark';
        setIsDark(nextDark);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  return { isDark, toggleTheme };
}
